/* Zugang zum Konto – die einzige Stelle im Frontend, die mit der Auth-API redet.

   ── Was hier bewusst NICHT mehr steht ────────────────────────────────────
   Früher lag das Sitzungs-Token im localStorage und wanderte als
   `Authorization: Bearer …` mit. Beides ist weg:

     • Der Server liefert das Sitzungs-Token gar nicht mehr aus. Es steckt in
       einem HttpOnly-Cookie, an das JavaScript nicht herankommt – eine
       XSS-Lücke kann die Sitzung damit nicht mehr stehlen.
     • Daneben gab es ein zweites, lokales Konto-System (Konten in IndexedDB,
       Passwörter als einfacher SHA-256, „Anmeldung" per localStorage-Eintrag).
       Ein zweites Auth-System ist immer das schwächere – es ist ersatzlos raus.

   Geblieben ist ein CSRF-Token. Es lebt nur in dieser Modulvariablen, also im
   Arbeitsspeicher: ein Neuladen der Seite holt sich über /auth/me ein frisches.
   Für sich allein ist es wertlos – ohne das Cookie öffnet es nichts. */

/* Ohne API läuft nichts mehr. In der lokalen Entwicklung zeigt die App auf den
   Worker unter `wrangler dev`: localhost:4177 und localhost:8787 sind
   dieselbe Site, damit trägt das SameSite=Lax-Cookie. Gegen die Live-API zu
   entwickeln ginge nur mit einem zweiten Anmeldeweg – genau den wollten wir los. */
const DEV = typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(location.hostname);
const konfiguriert = typeof window !== 'undefined' && window.MIMORIO_API && window.MIMORIO_API.trim();

export const API_BASE = DEV
  ? (window.MIMORIO_API_DEV || 'http://localhost:8787').replace(/\/+$/, '')
  : String(konfiguriert || '').replace(/\/+$/, '');
export const CLOUD = !!API_BASE;

let csrf = '';
let signedIn = false;

export const hasSession = () => signedIn;
export const markSignedIn = (v) => { signedIn = !!v; if (!v) csrf = ''; };

export async function apiFetch(path, opts = {}) {
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  // Nur veränderende Aufrufe brauchen das CSRF-Token; der Server verlangt es
  // auch nur dort.
  if ((opts.method || 'GET').toUpperCase() !== 'GET' && csrf) headers['X-CSRF-Token'] = csrf;
  // credentials: 'include' ist Pflicht – sonst schickt der Browser das Cookie
  // an eine andere Herkunft nicht mit.
  const res = await fetch(API_BASE + path, { ...opts, headers, credentials: 'include' });
  let data = {};
  try { data = await res.json(); } catch { /* leer */ }
  if (!res.ok) {
    const err = new Error(data.error || ('Fehler ' + res.status));
    err.status = res.status;
    throw err;
  }
  // Jede Antwort darf ein frisches CSRF-Token nachliefern (etwa nach einer
  // Sitzungsrotation beim Passwortwechsel).
  if (data.csrf) csrf = data.csrf;
  return data;
}
const post = (path, body) => apiFetch(path, { method: 'POST', body: JSON.stringify(body || {}) });

function uebernehmen(d) {
  if (d.csrf) csrf = d.csrf;
  signedIn = true;
  return d.user;
}

/* ── Anmeldewege ───────────────────────────────────────────────────────── */

/* Registrieren meldet NICHT an. Der Zugang entsteht erst, wenn die Adresse
   bestätigt ist – sonst ließe sich ein Konto mit fremder Adresse benutzen.
   Die Antwort ist außerdem immer dieselbe, auch wenn es die Adresse schon
   gibt: was Sache ist, steht in der Mail an genau diese Adresse. */
export const cloudRegister = (email, password, username) =>
  post('/auth/register', { email, password, username });

export async function cloudLogin(email, password, remember = true) {
  return uebernehmen(await post('/auth/login', { email, password, remember }));
}

/* Einmal-Kennung für Google/Apple. Sie wandert in das ID-Token und darf genau
   einmal eingelöst werden – ein abgefangenes Token ist danach wertlos. */
export const cloudNonce = async () => (await post('/auth/nonce')).nonce;

/* Gibt zusätzlich zurück, ob dabei ein Konto NEU entstanden ist. Nur dann
   fragt die App nach einem Nutzernamen – bei jeder weiteren Anmeldung wäre
   das eine Belästigung. */
export async function cloudOAuth(provider, body, remember = true) {
  const d = await post('/auth/' + provider, { ...body, remember });
  return { user: uebernehmen(d), neu: !!d.neu };
}
export const cloudLinkProvider = (provider, body) => post('/auth/link/' + provider, body);
export const cloudUnlinkProvider = (provider) => post('/auth/unlink', { provider });

export async function cloudSetPassword(current, password) {
  return uebernehmen(await post('/auth/password', { current, password }));
}

export const cloudForgot = (email) => post('/auth/forgot', { email });

export async function cloudResetPassword(token, password) {
  return uebernehmen(await post('/auth/reset', { token, password }));
}

/* Bestätigung der Adresse öffnet zugleich die App. */
export async function cloudVerifyEmail(token) {
  return uebernehmen(await post('/auth/verify-email', { token }));
}
export const cloudResendVerification = () => post('/auth/resend-verification', {});

/* ── Adresse wechseln ── */
export const cloudRequestEmailChange = (email, current) => post('/auth/email', { email, current });
export const cloudConfirmEmailChange = (token) => post('/auth/email/confirm', { token });

/* ── Geräte und Protokoll ── */
export const cloudSessions = () => apiFetch('/auth/sessions');
export const cloudRevokeSession = (ref) => post('/auth/sessions/revoke', { ref });
export const cloudLogoutAll = () => post('/auth/logout-all', {});
export const cloudEvents = () => apiFetch('/auth/events');

/* Welche Anbieter der Server anbietet – einmal geladen und gemerkt. */
let authConfig = null;
export async function cloudAuthConfig() {
  if (authConfig) return authConfig;
  try { authConfig = await apiFetch('/auth/config'); }
  catch { authConfig = { google: false, apple: false }; }
  return authConfig;
}

export async function cloudMe() {
  const d = await apiFetch('/auth/me');
  return uebernehmen(d);
}

export async function cloudLogout() {
  try { await post('/auth/logout', {}); } catch { /* egal */ }
  markSignedIn(false);
}

export const cloudDeleteAccount = (current) => post('/account/delete', { current });
